import React, { useEffect, useRef, useState } from 'react';
import { 
  Percent, 
  Tag, 
  TrendingUp, 
  DollarSign, 
  UtensilsCrossed, 
  Users, 
  Receipt, 
  Clock, 
  Banknote, 
  Calculator, 
  CreditCard, 
  FileText, 
  Building2, 
  RefreshCw, 
  LineChart, 
  ArrowRightLeft, 
  Home, 
  Calendar, 
  TrendingDown, 
  PiggyBank, 
  Wallet, 
  UserCheck, 
  Flame, 
  ArrowRight, 
  Search,
  CheckCircle2
} from 'lucide-react';
import { ToolCategory, ToolItem } from '../../types';

interface MegaMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTool: (toolId: string) => void;
  onViewAllTools: () => void;
  onOpenSearch?: (initialQuery?: string) => void;
  tools: ToolItem[];
}

// Canonical 5 families strictly ordered
const CANONICAL_CATEGORIES: { id: ToolCategory; label: string }[] = [
  { id: 'Everyday Money', label: 'EVERYDAY MONEY' },
  { id: 'Income & Tax', label: 'INCOME & TAX' },
  { id: 'Currency & Transfers', label: 'CURRENCY & TRANSFERS' },
  { id: 'Borrow', label: 'BORROW' },
  { id: 'Save & Invest', label: 'SAVE & INVEST' },
];

export const MegaMenu: React.FC<MegaMenuProps> = ({
  isOpen,
  onClose,
  onSelectTool,
  onViewAllTools,
  onOpenSearch,
  tools,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);

  // Filter only published tools
  const publishedTools = tools.filter((t) => (t.status ? t.status === 'PUBLISHED' : true));

  // Flattened array of visible items for smooth keyboard arrow navigation
  const visibleToolsList = CANONICAL_CATEGORIES.flatMap((cat) =>
    publishedTools.filter((t) => t.category === cat.id)
  );

  // Focus trap / escape listener & click outside
  useEffect(() => {
    if (!isOpen) {
      setFocusedIndex(-1);
      setSearchQuery('');
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }

      if (visibleToolsList.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex((prev) => (prev + 1) % visibleToolsList.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex((prev) => (prev - 1 + visibleToolsList.length) % visibleToolsList.length);
      } else if (e.key === 'Home') {
        e.preventDefault();
        setFocusedIndex(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        setFocusedIndex(visibleToolsList.length - 1);
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, visibleToolsList.length]);

  if (!isOpen) return null;

  const renderToolIcon = (iconName: string) => {
    const props = { className: 'w-4 h-4 text-[#2563EB] shrink-0' };
    switch (iconName) {
      case 'Percent':
        return <Percent {...props} />;
      case 'Tag':
        return <Tag {...props} />;
      case 'TrendingUp':
        return <TrendingUp {...props} />;
      case 'DollarSign':
        return <DollarSign {...props} />;
      case 'UtensilsCrossed':
        return <UtensilsCrossed {...props} />;
      case 'Users':
        return <Users {...props} />;
      case 'Receipt':
        return <Receipt {...props} />;
      case 'Clock':
        return <Clock {...props} />;
      case 'Banknote':
        return <Banknote {...props} />;
      case 'Calculator':
        return <Calculator {...props} />;
      case 'CreditCard':
        return <CreditCard {...props} />;
      case 'FileText':
        return <FileText {...props} />;
      case 'Building2':
        return <Building2 {...props} />;
      case 'RefreshCw':
        return <RefreshCw {...props} />;
      case 'LineChart':
        return <LineChart {...props} />;
      case 'ArrowRightLeft':
        return <ArrowRightLeft {...props} />;
      case 'Home':
        return <Home {...props} />;
      case 'Calendar':
        return <Calendar {...props} />;
      case 'TrendingDown':
        return <TrendingDown {...props} />;
      case 'PiggyBank':
        return <PiggyBank {...props} />;
      case 'Wallet':
        return <Wallet {...props} />;
      case 'UserCheck':
        return <UserCheck {...props} />;
      case 'Flame':
        return <Flame {...props} />;
      default:
        return <TrendingUp {...props} />;
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onOpenSearch) {
      onOpenSearch(searchQuery);
    } else {
      onViewAllTools();
    }
    onClose();
  };

  return (
    <div
      ref={menuRef}
      id="yamoopay-tools-megamenu"
      role="menu"
      aria-label="YamooPay Public Tools Directory"
      className="absolute top-full left-0 w-full bg-white border-b border-[#E2E8F0] shadow-2xl z-50 animate-in fade-in slide-in-from-top-1 duration-150"
      onMouseLeave={onClose}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-6 space-y-8">
        {/* 5-Column Canonical Category Layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-8 gap-y-6">
          {CANONICAL_CATEGORIES.map((cat) => {
            const categoryTools = publishedTools.filter((t) => t.category === cat.id);
            return (
              <div key={cat.id} className="space-y-4">
                {/* Uppercase Category Header */}
                <h3 className="text-[11px] font-bold uppercase tracking-wider text-[#64748B] select-none">
                  {cat.label}
                </h3>

                {/* Tool Items in Category */}
                <ul className="space-y-3" role="none">
                  {categoryTools.map((tool) => {
                    const isGlobalIndex =
                      visibleToolsList.findIndex((item) => item.id === tool.id) === focusedIndex;
                    return (
                      <li key={tool.id} role="none">
                        <button
                          role="menuitem"
                          onClick={() => {
                            onSelectTool(tool.id);
                            onClose();
                          }}
                          className={`w-full text-left flex items-start gap-3 p-1.5 -ml-1.5 rounded-lg transition-colors group cursor-pointer ${
                            isGlobalIndex ? 'bg-[#EFF6FF] ring-1 ring-[#2563EB]' : 'hover:bg-[#F8FAFC]'
                          }`}
                        >
                          <div className="mt-0.5 w-6 h-6 rounded-md bg-[#EFF6FF] group-hover:bg-[#DBEAFE] flex items-center justify-center transition-colors">
                            {renderToolIcon(tool.iconName)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-[13px] font-bold text-[#1E293B] group-hover:text-[#2563EB] transition-colors leading-snug truncate">
                              {tool.name || tool.label}
                            </div>
                            <div className="text-[11px] text-[#64748B] leading-tight truncate">
                              {tool.subtitle || tool.tagline}
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Bottom Actions Bar */}
        <div className="border-t border-[#E2E8F0] pt-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Left: Browse all tools link */}
          <button
            onClick={() => {
              onViewAllTools();
              onClose();
            }}
            className="text-[13px] font-bold text-[#2563EB] hover:text-[#1D4ED8] flex items-center gap-1.5 group transition-colors cursor-pointer self-start sm:self-auto"
          >
            <span>Browse all tools</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>

          {/* Right: Search tools input */}
          <form
            onSubmit={handleSearchSubmit}
            className="w-full sm:w-80 flex items-center bg-[#F1F5F9] hover:bg-[#E2E8F0]/80 focus-within:bg-white border border-transparent focus-within:border-[#2563EB] focus-within:ring-2 focus-within:ring-[#2563EB]/10 rounded-xl px-3.5 py-1.5 transition-all"
          >
            <Search className="w-4 h-4 text-[#64748B] shrink-0 mr-2.5" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search tools..."
              aria-label="Search tools"
              className="w-full bg-transparent border-none text-xs font-medium text-[#1E293B] placeholder-[#94A3B8] focus:outline-none"
            />
            {searchQuery && (
              <button
                type="submit"
                className="text-[11px] font-bold text-[#2563EB] hover:underline shrink-0"
              >
                Go
              </button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};
